import React from 'react'
import { useSelector } from 'react-redux'
import Currency from '../../../../currency'
import InvoiceItemsTable from './InvoiceItemsTable'
import { formatBerlinDate, getGrossTotals, getInvoiceRows, getNumberLocale, getPostPaymentGrossTotal, getPostPaymentNetTotal, getPostPaymentVatRows, getVatRows, sumTips } from './helper'
import { getPaymentMethodNames } from '../../helper'
import { getFormattedCurrency, getNeededDateFormat } from '../../../../../../helper'
import { selectCurrencySymbol } from '../../../../../../redux/selector/system'
import { getInvoiceViewStatus, INVOICE_STATUS, isPostPaymentInvoice } from '../../constants'
import { InvoiceSheetContent, InvoiceSheetFooter, InvoiceSheetLabel, InvoiceSheetParagraph, InvoiceSheetRow, InvoiceSheetTextBlock, InvoiceSheetTitle, InvoiceSheetTotalGross, InvoiceSheetTotalLines, InvoiceSheetTotals } from './index.styled'

const InvoiceSheet = React.memo(({ invoice, t, lng, layout }) => {
	const currency = useSelector(selectCurrencySymbol)
	const label = key => t(`invoices.modal.invoiceDataModal.${key}`, lng)
	const company = invoice.company
	const billingAddress = invoice.customer.billingAddress
	const order = invoice.order
	const viewStatus = getInvoiceViewStatus(invoice)
	const isCanceled = viewStatus === INVOICE_STATUS.CANCELED
	const isPostPayment = isPostPaymentInvoice(invoice)
	const isPaymentDetailsHidden = isPostPayment || viewStatus === INVOICE_STATUS.PAID
	const numberLocale = getNumberLocale(lng?.lng)
	const isSmallBusiness = order?.taxTotal?.businessType === 'SMALL'
	const isMobile = layout === 'mobile'
	const rowLayout = isMobile ? 'mobile' : ''
	const email = company.ordersEmail || company.ownerEmail
	const exchangeRate = order?.exchangeRateMetaData?.rate
	const exchangeCurrency = order?.exchangeRateMetaData?.currency
	const rows = getInvoiceRows(order, t, lng, isPostPayment)
	const vatRows = isPostPayment ? getPostPaymentVatRows(order) : getVatRows(order)
	const grossTotals = isPostPayment ? [getPostPaymentGrossTotal(order)] : getGrossTotals(order)
	const tips = isPostPayment ? sumTips(order?.purchaseItems) : 0
	const tableLabels = {
		quantity: label('table.quantity'),
		name: label('table.name'),
		unitPrice: label('table.unitPrice'),
		total: label('table.total'),
	}

	return (
		<InvoiceSheetContent className={layout}>
			<InvoiceSheetRow className={rowLayout}>
				<InvoiceSheetTextBlock>
					<div>{company.name}</div>
					<div>{company.street} {company.house}</div>
					<div>{company.zipCode} {company.city}</div>
				</InvoiceSheetTextBlock>
				<InvoiceSheetTextBlock className={'end ' + rowLayout}>
					<div>{label('invoiceNumber')}: {invoice.invoiceNumber}</div>
					<div>{label('invoiceDate')}: {getNeededDateFormat(invoice.createDate)}</div>
					{isPostPayment && <div>{label('serviceDate')}: {formatBerlinDate(order?.orderDate)}</div>}
					<div>{label('index')}: {invoice.customer.customerNumber}</div>
					<div>{label('orderNumberTitle')}: {order?.orderDayCounter}</div>
					{invoice.correctedInvoiceNumber && <div>{t('invoices.modal.invoiceDataModal.correctedInvoice', { ...lng, number: invoice.correctedInvoiceNumber })}</div>}
				</InvoiceSheetTextBlock>
			</InvoiceSheetRow>
			<InvoiceSheetRow className={rowLayout}>
				<InvoiceSheetTextBlock>
					{billingAddress.companyName && <div>{billingAddress.companyName}</div>}
					<div>{billingAddress.name}</div>
					<div>{billingAddress.street} {billingAddress.streetNumber}</div>
					<div>{billingAddress.postalCode} {billingAddress.city}</div>
				</InvoiceSheetTextBlock>
				{isCanceled &&
					<InvoiceSheetTextBlock className={'end ' + rowLayout}>
						<div><InvoiceSheetLabel>{label('canceledAt')}:</InvoiceSheetLabel> {getNeededDateFormat(invoice.statusChangedDate)}</div>
					</InvoiceSheetTextBlock>}
			</InvoiceSheetRow>
			<InvoiceSheetTitle>{isCanceled ? label('canceledInvoice') : label('invoice')}</InvoiceSheetTitle>
			<InvoiceItemsTable rows={rows} labels={tableLabels} layout={layout} />
			<InvoiceSheetTotals>
				{grossTotals.map((total, i) =>
					<InvoiceSheetTotalGross key={'total_' + i}>{label('totalGross')}: <Currency>{total}</Currency></InvoiceSheetTotalGross>)}
				{tips > 0 && <div>{label('order.tips')}: <Currency>{tips}</Currency></div>}
				{order?.exchangeRateMetaData &&
					<InvoiceSheetTotalLines>
						{grossTotals.map((total, i) =>
							<div key={'total_currency_' + i}>
								{label('order.totalInCurrency')} {exchangeCurrency}: {getFormattedCurrency(Math.floor((total * exchangeRate) * 100) / 100, numberLocale, exchangeCurrency)}
							</div>)}
						<div>{label('order.exchangeRate')}: 1{currency} = {getFormattedCurrency(exchangeRate, numberLocale, exchangeCurrency)}</div>
					</InvoiceSheetTotalLines>}
				{isSmallBusiness &&
					<InvoiceSheetTotalLines>
						<div>{label('order.kleinunternehmer')}</div>
					</InvoiceSheetTotalLines>}
				{!isSmallBusiness && vatRows.length > 0 &&
					<InvoiceSheetTotalLines>
						<div>{label('includedVat')}:</div>
						{vatRows.map((row, i) =>
							<div key={'vat_' + i}>{row.title} {label('order.vat')}: <Currency>{row.vat}</Currency></div>)}
						{isPostPayment && <div>{label('order.netto')}: <Currency>{getPostPaymentNetTotal(grossTotals[0], vatRows, tips)}</Currency></div>}
					</InvoiceSheetTotalLines>}
			</InvoiceSheetTotals>
			{isCanceled ?
				<InvoiceSheetParagraph>
					<div>{label('canceledNotice.title')}</div>
					<div>{label('canceledNotice.text')}</div>
					<div>{label('canceledNotice.paymentNotRequired')}</div>
				</InvoiceSheetParagraph> :
				<>
					{isPostPayment &&
						<InvoiceSheetParagraph>
							<div>{t('invoices.modal.invoiceDataModal.paidOn', { ...lng, date: formatBerlinDate(order?.paidDate ?? order?.orderDate), methods: getPaymentMethodNames(order?.payments?.map(payment => payment.method), t, lng) })}</div>
						</InvoiceSheetParagraph>}
					{!isPaymentDetailsHidden &&
						<>
							<InvoiceSheetParagraph>
								<div>{label('paymentTerms')}</div>
							</InvoiceSheetParagraph>
							<InvoiceSheetParagraph>
								<div>{label('accountOwner')}: {company.name}</div>
								<div>{label('iban')}: {invoice.iban.number}</div>
								<div>{label('bic')}: {invoice.iban.bic}</div>
							</InvoiceSheetParagraph>
						</>}
				</>}
			<InvoiceSheetParagraph>
				<div>{label('regards')}</div>
				<div>{company.name}</div>
			</InvoiceSheetParagraph>
			<InvoiceSheetFooter>
				<div>{company.name}</div>
				<div>{company.street} {company.house}, {company.zipCode} {company.city}</div>
				<div>{label('taxNumber')}: {company.taxId}</div>
				<div>{label('phone')}: {company.phonePrefix} {company.phoneNumber}</div>
				<div>{label('email')}: {email}</div>
			</InvoiceSheetFooter>
		</InvoiceSheetContent>
	)
})

InvoiceSheet.displayName = 'InvoiceSheet'
export default InvoiceSheet
